import { useMemo } from "react";
import PropTypes from "prop-types";
import { Chip, TextField, InputAdornment, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useICCCColors } from "../components/theme";
import { categoryMeta, HEALTH_ORDER } from "../model/constants";

const HEALTH_LABEL = { healthy: "Healthy", warning: "Warning", critical: "Critical", unknown: "Unknown" };

// Doubles as the always-visible colour-coded status legend (statusCounts
// chips) and the interactive filter row — one source of truth for health
// counts instead of a second, duplicate status-bar component.
export default function MapFilters({
  devices,
  groupFilter,
  onGroupFilterChange,
  statusFilter,
  onStatusFilterChange,
  nameFilter,
  onNameFilterChange,
  ipFilter,
  onIpFilterChange,
}) {
  const ICCC_COLORS = useICCCColors();

  const groupCounts = useMemo(() => {
    const map = new Map();
    devices.forEach((d) => {
      map.set(d.category, (map.get(d.category) || 0) + 1);
    });
    return Array.from(map, ([code, count]) => ({ code, count, meta: categoryMeta(code) }));
  }, [devices]);

  const statusCounts = useMemo(() => {
    const map = new Map();
    devices.forEach((d) => {
      map.set(d.health, (map.get(d.health) || 0) + 1);
    });
    return HEALTH_ORDER.filter((h) => map.get(h)).map((h) => ({ health: h, count: map.get(h) }));
  }, [devices]);

  if (!devices.length) return null;

  return (
    <MDBox
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1,
        bgcolor: ICCC_COLORS.cardBg,
        border: `1px solid ${ICCC_COLORS.cardBorder}`,
        borderRadius: 1.5,
        px: 1.5,
        py: 1,
      }}
    >
      <TextField
        size="small"
        placeholder="Search by name"
        value={nameFilter}
        onChange={(e) => onNameFilterChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon fontSize="small" sx={{ color: ICCC_COLORS.textMuted, fontSize: 16 }}>
                search
              </Icon>
            </InputAdornment>
          ),
        }}
        sx={{
          width: 170,
          "& .MuiOutlinedInput-root": { bgcolor: ICCC_COLORS.cardBg2, fontSize: 12, height: 32 },
        }}
      />
      <TextField
        size="small"
        placeholder="Filter by IP"
        value={ipFilter}
        onChange={(e) => onIpFilterChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon fontSize="small" sx={{ color: ICCC_COLORS.textMuted, fontSize: 16 }}>
                lan
              </Icon>
            </InputAdornment>
          ),
        }}
        sx={{
          width: 150,
          "& .MuiOutlinedInput-root": { bgcolor: ICCC_COLORS.cardBg2, fontSize: 12, height: 32 },
        }}
      />

      {(groupCounts.length > 0 || statusCounts.length > 0) && (
        <MDBox sx={{ width: "1px", alignSelf: "stretch", bgcolor: ICCC_COLORS.divider, mx: 0.5 }} />
      )}

      {groupCounts.map(({ code, count, meta }) => (
        <Chip
          key={code}
          size="small"
          label={`${meta.label} (${count})`}
          onClick={() => onGroupFilterChange(groupFilter === code ? null : code)}
          sx={{
            bgcolor: groupFilter === code ? ICCC_COLORS.accent : ICCC_COLORS.cardBg2,
            color: groupFilter === code ? "#fff" : ICCC_COLORS.textSecondary,
            fontWeight: groupFilter === code ? 700 : 500,
          }}
        />
      ))}

      {groupCounts.length > 0 && statusCounts.length > 0 && (
        <MDBox sx={{ width: "1px", alignSelf: "stretch", bgcolor: ICCC_COLORS.divider, mx: 0.5 }} />
      )}

      {statusCounts.map(({ health, count }) => (
        <Chip
          key={health}
          size="small"
          // Always shows its health colour as a dot — this row doubles as the
          // colour-coded status legend, not just an on-click filter toggle.
          icon={
            <MDBox
              sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: ICCC_COLORS[health], ml: "6px !important" }}
            />
          }
          label={`${HEALTH_LABEL[health]} (${count})`}
          onClick={() => onStatusFilterChange(statusFilter === health ? null : health)}
          sx={{
            bgcolor: statusFilter === health ? ICCC_COLORS[health] : ICCC_COLORS.cardBg2,
            color: statusFilter === health ? "#fff" : ICCC_COLORS.textSecondary,
            fontWeight: statusFilter === health ? 700 : 500,
            border: `1px solid ${statusFilter === health ? ICCC_COLORS[health] : ICCC_COLORS.cardBorder}`,
          }}
        />
      ))}

      {(groupFilter || statusFilter || nameFilter || ipFilter) && (
        <MDTypography
          variant="caption"
          onClick={() => {
            onGroupFilterChange(null);
            onStatusFilterChange(null);
            onNameFilterChange("");
            onIpFilterChange("");
          }}
          sx={{ color: ICCC_COLORS.accent, cursor: "pointer", ml: "auto", fontWeight: 600 }}
        >
          Clear filters
        </MDTypography>
      )}
    </MDBox>
  );
}

MapFilters.propTypes = {
  devices: PropTypes.arrayOf(PropTypes.object).isRequired,
  groupFilter: PropTypes.string,
  onGroupFilterChange: PropTypes.func.isRequired,
  statusFilter: PropTypes.string,
  onStatusFilterChange: PropTypes.func.isRequired,
  nameFilter: PropTypes.string,
  onNameFilterChange: PropTypes.func.isRequired,
  ipFilter: PropTypes.string,
  onIpFilterChange: PropTypes.func.isRequired,
};

MapFilters.defaultProps = {
  groupFilter: null,
  statusFilter: null,
  nameFilter: "",
  ipFilter: "",
};
